"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Progress } from "../ui/progress";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  CheckCircle,
  Circle,
  Clock,
  AlertCircle,
  Calculator,
  Building,
  Shield,
  FileCheck,
  BarChart3,
  FileText,
} from "lucide-react";
import { getCompanyName } from "@/lib/textron-data-processor";
import EngagementAcceptanceData from "../../constants/EngagementAcceptance.json";
import UnderstandingEntityData from "../../constants/understandingEntity.json";
import FraudRiskData from "../../constants/FraudRisk.json";

interface WorkflowStatus {
  id: string;
  title: string;
  description: string;
  progress: number;
  status: "not-started" | "in-progress" | "completed";
  questionsAnswered: number;
  totalQuestions: number;
  icon: React.ComponentType<any>;
}

interface PlanningDashboardProps {
  onNavigateToWorkflow: (workflowId: string) => void;
}

export function PlanningDashboard({
  onNavigateToWorkflow,
}: PlanningDashboardProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [sectionStats, setSectionStats] = useState<
    Record<
      string,
      {
        answered: number;
        total: number;
        progress: number;
      }
    >
  >({});

  // Load company name first
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Helper to determine if a single planning response is "answered"
  const isResponseAnswered = (resp: any): boolean => {
    if (!resp || typeof resp !== "object") return false;

    const rawAnswer = resp.answer ?? resp.selectedOption;
    const hasAnswer =
      rawAnswer !== null &&
      typeof rawAnswer !== "undefined" &&
      !(
        typeof rawAnswer === "string" &&
        rawAnswer.trim().length === 0
      ) &&
      !(
        Array.isArray(rawAnswer) &&
        rawAnswer.length === 0
      );

    const textResponse = resp.textResponse;
    const hasTextResponse =
      !!textResponse &&
      (
        (typeof textResponse === "string" &&
          textResponse.trim().length > 0) ||
        (textResponse.html &&
          String(textResponse.html).trim().length > 0) ||
        (textResponse.plainText &&
          String(textResponse.plainText).trim().length > 0)
      );

    const hasDocuments =
      Array.isArray(resp.documents) && resp.documents.length > 0;

    const hasTable =
      Array.isArray(resp.table) && resp.table.length > 0;

    return !!(hasAnswer || hasTextResponse || hasDocuments || hasTable);
  };

  const collectQuestionIds = (root: any): string[] => {
    const ids = new Set<string>();

    const traverse = (node: any) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(traverse);
        return;
      }
      if (typeof node === "object") {
        if (typeof node.id === "string") {
          ids.add(node.id);
        }
        if (node.questions) {
          traverse(node.questions);
        }
        Object.values(node).forEach((value) => {
          if (value && typeof value === "object") {
            traverse(value);
          }
        });
      }
    };

    traverse(root);
    return Array.from(ids);
  };

  // Load planning responses per section from Planning_UserResponses.json via planningAPI
  useEffect(() => {
    if (!companyName) return;

    const loadSectionStats = async () => {
      setLoading(true);

      try {
        const sp = (window as any).sharePointAPI;
        if (!sp?.downloadClientJson) {
          console.warn("sharePointAPI.downloadClientJson is not available");
          setLoading(false);
          return;
        }

        const result = await sp.downloadClientJson("Planning_UserResponses.json");
        if (!result?.success || !result.data) {
          console.warn("Failed to load Planning_UserResponses.json");
          setLoading(false);
          return;
        }

        const data = result.data;
        const dataArray = Array.isArray(data) ? data : [data];

        const allResponses: Record<string, any> = {};

        // Common shape: array of entries with { companyName, sectionName, responses }
        for (const entry of dataArray) {
          if (!entry || typeof entry !== "object") continue;

          if (entry.companyName && entry.companyName !== companyName) continue;

          const responsesObj =
            entry.responses && typeof entry.responses === "object"
              ? entry.responses
              : undefined;

          if (responsesObj) {
            for (const [qid, resp] of Object.entries(responsesObj)) {
              allResponses[qid] = resp;
            }
          }
        }

        // Fallback: map-like structure { [companyName]: { ...questionId: resp } }
        if (Object.keys(allResponses).length === 0 && data && typeof data === "object") {
          const companyData = (data as any)[companyName];
          if (companyData) {
            const maybeResponses =
              companyData.responses && typeof companyData.responses === "object"
                ? companyData.responses
                : companyData;
            if (maybeResponses && typeof maybeResponses === "object") {
              for (const [qid, resp] of Object.entries(maybeResponses)) {
                allResponses[qid] = resp;
              }
            }
          }
        }

        const engagementAcceptanceIds = collectQuestionIds(
          (EngagementAcceptanceData as any)["Engagement Acceptance & Continuance"],
        );
        const understandingEntityIds = collectQuestionIds(
          (UnderstandingEntityData as any)["Understanding the Entity and its Environment"],
        );
        const fraudRiskIds = collectQuestionIds(
          (FraudRiskData as any)["Fraud Risk Assessment & Response"],
        );

        const sectionConfig: {
          sectionKey: string;
          total: number;
          prefixes: string[];
          ids?: string[];
        }[] = [
          {
            sectionKey: "materiality",
            total: 8,
            prefixes: ["M-", "revisedMateriality"],
          },
          {
            sectionKey: "understanding-entity",
            total: understandingEntityIds.length || 42,
            prefixes: [],
            ids: understandingEntityIds,
          },
          {
            sectionKey: "engagement-acceptance",
            total: engagementAcceptanceIds.length || 17,
            prefixes: [],
            ids: engagementAcceptanceIds,
          },
          {
            sectionKey: "fraud-risk",
            total: fraudRiskIds.length || 15,
            prefixes: [],
            ids: fraudRiskIds,
          },
          {
            sectionKey: "it-risk",
            total: 2,
            prefixes: ["IT-", "ITR-"],
          },
          {
            sectionKey: "preliminary-analytical",
            total: 1,
            prefixes: ["AP-"],
          },
        ];

        const matchesPrefixes = (id: string, prefixes: string[]) =>
          prefixes.some((p) => (id || "").startsWith(p));

        const nextStats: typeof sectionStats = {};

        for (const { sectionKey, total, prefixes, ids } of sectionConfig) {
          const relevantEntries =
            ids && ids.length
              ? Object.entries(allResponses).filter(([qid]) =>
                  ids.includes(qid),
                )
              : Object.entries(allResponses).filter(([qid]) =>
                  matchesPrefixes(qid, prefixes),
                );

          const answeredRaw = relevantEntries.filter(([, resp]) =>
            isResponseAnswered(resp),
          ).length;

          const answered = Math.min(answeredRaw, total);

          nextStats[sectionKey] = {
            answered,
            total,
            progress: total > 0 ? (answered / total) * 100 : 0,
          };
        }

        setSectionStats(nextStats);
      } catch (error) {
        console.warn("Failed to load planning dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    loadSectionStats();
  }, [companyName]);

  const workflows: WorkflowStatus[] = useMemo(
    () => [
      {
        id: "planning-materiality",
        title: "Materiality Planning",
        description: "Initial materiality benchmarks and calculations",
        icon: Calculator,
        ...(() => {
          const stat = sectionStats["materiality"] || {
            answered: 0,
            total: 8,
            progress: 0,
          };
          return {
            progress: stat.progress,
            questionsAnswered: stat.answered,
            totalQuestions: stat.total,
            status:
              stat.progress === 0
                ? "not-started"
                : stat.progress === 100
                  ? "completed"
                  : "in-progress",
          };
        })(),
      },
      {
        id: "planning-understanding-entity",
        title: "Understanding Entity",
        description: "Entity environment and internal controls assessment",
        icon: Building,
        ...(() => {
          const stat = sectionStats["understanding-entity"] || {
            answered: 0,
            total: 42,
            progress: 0,
          };
          return {
            progress: stat.progress,
            questionsAnswered: stat.answered,
            totalQuestions: stat.total,
            status:
              stat.progress === 0
                ? "not-started"
                : stat.progress === 100
                  ? "completed"
                  : "in-progress",
          };
        })(),
      },
      {
        id: "planning-engagement-acceptance",
        title: "Engagement Acceptance",
        description: "Engagement profiling and acceptance procedures",
        icon: FileCheck,
        ...(() => {
          const stat = sectionStats["engagement-acceptance"] || {
            answered: 0,
            total: 17,
            progress: 0,
          };
          return {
            progress: stat.progress,
            questionsAnswered: stat.answered,
            totalQuestions: stat.total,
            status:
              stat.progress === 0
                ? "not-started"
                : stat.progress === 100
                  ? "completed"
                  : "in-progress",
          };
        })(),
      },
      {
        id: "planning-fraud-risk",
        title: "Fraud Risk Assessment",
        description: "Fraud risk identification and response procedures",
        icon: Shield,
        ...(() => {
          const stat = sectionStats["fraud-risk"] || {
            answered: 0,
            total: 15,
            progress: 0,
          };
          return {
            progress: stat.progress,
            questionsAnswered: stat.answered,
            totalQuestions: stat.total,
            status:
              stat.progress === 0
                ? "not-started"
                : stat.progress === 100
                  ? "completed"
                  : "in-progress",
          };
        })(),
      },
      {
        id: "planning-it-risk",
        title: "IT Risk Assessment",
        description: "Information technology risk evaluation",
        icon: Shield,
        ...(() => {
          const stat = sectionStats["it-risk"] || {
            answered: 0,
            total: 2,
            progress: 0,
          };
          return {
            progress: stat.progress,
            questionsAnswered: stat.answered,
            totalQuestions: stat.total,
            status:
              stat.progress === 0
                ? "not-started"
                : stat.progress === 100
                  ? "completed"
                  : "in-progress",
          };
        })(),
      },
      {
        id: "planning-preliminary-analytical",
        title: "Preliminary Analytics",
        description: "Analytical procedures and variance analysis",
        icon: BarChart3,
        ...(() => {
          const stat = sectionStats["preliminary-analytical"] || {
            answered: 0,
            total: 1,
            progress: 0,
          };
          return {
            progress: stat.progress,
            questionsAnswered: stat.answered,
            totalQuestions: stat.total,
            status:
              stat.progress === 0
                ? "not-started"
                : stat.progress === 100
                  ? "completed"
                  : "in-progress",
          };
        })(),
      },
    ],
    [sectionStats],
  );

  const overallProgress =
    workflows.reduce((sum, workflow) => sum + workflow.progress, 0) /
    workflows.length;
  const completedWorkflows = workflows.filter(
    (w) => w.status === "completed",
  ).length;
  const inProgressWorkflows = workflows.filter(
    (w) => w.status === "in-progress",
  ).length;

  const getStatusIcon = (status: WorkflowStatus["status"]) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "in-progress":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: WorkflowStatus["status"]) => {
    switch (status) {
      case "completed":
        return (
          <Badge
            variant="default"
            className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
          >
            Completed
          </Badge>
        );
      case "in-progress":
        return (
          <Badge
            variant="secondary"
            className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
          >
            In Progress
          </Badge>
        );
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
          <p className="text-muted-foreground">Loading planning workflows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-left space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-3xl font-bold text-white">
              Audit Planning Status
            </h1>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-glass-blue border border-blue-500/20">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></div>
              <span className="text-xs font-medium text-blue-300">Planning Phase Active</span>
            </div>
          </div>
          <p className="text-muted-foreground text-lg">
            Comprehensive planning status overview for {companyName} audit engagement
          </p>
        </div>
      </div>

      {/* Key Metrics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
        {/* Overall Progress */}
        <Card className="glass-card hover:bg-glass-blue/10 transition-all duration-300 group">
          <CardContent className="p-6">
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <BarChart3 className="h-4 w-4 text-blue-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground">Overall Progress</h3>
                </div>
                <div className="text-4xl font-semibold text-white mb-4">
                  {Math.round(overallProgress)}%
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-300 text-xs font-medium border border-blue-500/20">
                  {completedWorkflows}/{workflows.length}
                </span>
                <span className="text-xs text-muted-foreground">sections completed</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completed Workflows */}
        <Card className="glass-card hover:bg-glass-green/10 transition-all duration-300 group">
          <CardContent className="p-6">
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground">Completed</h3>
                </div>
                <div className="text-4xl font-semibold text-white mb-4">
                  {completedWorkflows.toString().padStart(2, '0')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-md bg-green-500/10 text-green-300 text-xs font-medium border border-green-500/20">
                  {completedWorkflows}/{workflows.length}
                </span>
                <span className="text-xs text-muted-foreground">sections completed</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* In Progress */}
        <Card className="glass-card hover:bg-glass-yellow/10 transition-all duration-300 group">
          <CardContent className="p-6">
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-yellow-500/10 group-hover:bg-yellow-500/20 transition-colors">
                    <Clock className="h-4 w-4 text-yellow-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground">In Progress</h3>
                </div>
                <div className="text-4xl font-semibold text-white mb-4">
                  {inProgressWorkflows.toString().padStart(2, '0')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-md bg-yellow-500/10 text-yellow-300 text-xs font-medium border border-yellow-500/20">
                  {inProgressWorkflows}
                </span>
                <span className="text-xs text-muted-foreground">Active Sessions</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending */}
        <Card className="glass-card hover:bg-glass-gray/10 transition-all duration-300 group">
          <CardContent className="p-6">
            <div className="flex flex-col h-full justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-gray-500/10 group-hover:bg-gray-500/20 transition-colors">
                    <Circle className="h-4 w-4 text-gray-400" />
                  </div>
                  <h3 className="text-sm font-medium text-muted-foreground">Pending</h3>
                </div>
                <div className="text-4xl font-semibold text-white mb-4">
                  {(workflows.length - completedWorkflows - inProgressWorkflows).toString().padStart(2, '0')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-md bg-gray-500/10 text-gray-300 text-xs font-medium border border-gray-500/20">
                  {workflows.length - completedWorkflows - inProgressWorkflows}/{workflows.length}
                </span>
                <span className="text-xs text-muted-foreground">Pending Sessions</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Planning Sections Status */}
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold text-white">Planning Sections Status</h2>
          <p className="text-muted-foreground">Current Status Of All Planning Workflow Sections</p>
        </div>

        <div className="space-y-3">
          {workflows.map((workflow, index) => {
            const IconComponent = workflow.icon;
            return (
              <div
                key={workflow.id}
                className="group relative overflow-hidden rounded-xl border border-white/5 bg-white/5 p-4 transition-all hover:bg-white/10 hover:border-white/10 cursor-pointer"
                onClick={() => onNavigateToWorkflow(workflow.id)}
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Left Section: Icon & Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/5 group-hover:bg-white/10 transition-colors border border-white/5">
                      <IconComponent className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white text-base mb-1">{workflow.title}</h4>
                      <p className="text-sm text-muted-foreground">Current Status Of All Planning Workflow Sections</p>
                    </div>
                  </div>

                  {/* Right Section: Stats & Status */}
                  <div className="flex items-center gap-8">
                    <div className="text-right min-w-[120px]">
                      <div className="text-sm text-white mb-0.5">
                        {workflow.questionsAnswered} / {workflow.totalQuestions} Questions
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {Math.round(workflow.progress)}% Complete
                      </div>
                    </div>

                    <div className="min-w-[100px] flex justify-end">
                      {getStatusBadge(workflow.status)}
                    </div>

                    <div className="h-6 w-6 rounded-full border border-white/20 flex items-center justify-center group-hover:border-blue-400/50 transition-colors">
                      <div className="h-2 w-2 rounded-full bg-transparent group-hover:bg-blue-400 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
